import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import './ConstellationsView.css';

const ConstellationsView = ({ poem, onBack }) => {
  const [starsRevealed, setStarsRevealed] = useState(false);
  const [wordsFormed, setWordsFormed] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    // Reset animation states when poem changes
    setStarsRevealed(false);
    setWordsFormed(false);

    // Stars twinkle in
    const starsTimer = setTimeout(() => {
      setStarsRevealed(true);
    }, 1000);

    // Words form from stars
    const wordsTimer = setTimeout(() => {
      setWordsFormed(true);
    }, 3000);

    // Play occasional crystal sounds
    const soundInterval = setInterval(() => {
      if (audioRef.current) {
        audioRef.current.currentTime = 0;
        audioRef.current.play().catch(() => {});
      }
    }, 8000);

    return () => {
      clearTimeout(starsTimer);
      clearTimeout(wordsTimer);
      clearInterval(soundInterval);
    };
  }, [poem?.id]); // Only re-run when poem ID changes

  // Split poem into words for constellation formation
  const lines = poem?.content?.split('\n') || [];

  // Generate random star positions for background
  const backgroundStars = Array.from({ length: 80 }, (_, i) => ({
    id: i,
    x: Math.random() * 100,
    y: Math.random() * 100,
    size: Math.random() * 2 + 0.5,
    delay: Math.random() * 2
  }));

  return (
    <motion.div
      className="constellations-view"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 2 }}
    >
      {/* Night sky background */}
      <div className="night-sky-background">
        <div className="sky-gradient" />

        {/* Background stars twinkling */}
        {starsRevealed && backgroundStars.map(star => (
          <motion.div
            key={star.id}
            className="background-star"
            style={{
              left: `${star.x}%`,
              top: `${star.y}%`,
              width: `${star.size}px`,
              height: `${star.size}px`
            }}
            initial={{ opacity: 0, scale: 0 }}
            animate={{
              opacity: [0, 1, 0.7, 1],
              scale: [0, 1.2, 1]
            }}
            transition={{
              delay: star.delay,
              duration: 1.5,
              opacity: {
                repeat: Infinity,
                duration: 3 + Math.random() * 2,
                ease: "easeInOut"
              }
            }}
          />
        ))}
      </div>

      {/* Title constellation */}
      <motion.div
        className="constellation-title"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5, duration: 2 }}
      >
        <h2>{poem?.title || 'Untitled'}</h2>
      </motion.div>

      {/* Poem content as constellations */}
      <div className="constellations-content">
        {lines.map((line, lineIndex) => (
          <motion.div
            key={lineIndex}
            className="constellation-line"
            initial={{ opacity: 0 }}
            animate={{ opacity: wordsFormed ? 1 : 0 }}
            transition={{ delay: 3 + lineIndex * 1.2, duration: 2 }}
          >
            {line.split(' ').map((word, wordIndex) => (
              <motion.span
                key={`${lineIndex}-${wordIndex}`}
                className="constellation-word"
                initial={{ opacity: 0 }}
                animate={{ opacity: wordsFormed ? 1 : 0 }}
                transition={{
                  delay: 3 + lineIndex * 1.2 + wordIndex * 0.3,
                  duration: 2
                }}
              >
                {/* The word itself made of stars - using text with special styling */}
                <motion.span
                  className="star-formed-word"
                  initial={{ opacity: 0, scale: 0.5 }}
                  animate={{
                    opacity: wordsFormed ? 1 : 0,
                    scale: wordsFormed ? 1 : 0.5
                  }}
                  transition={{
                    delay: 3.5 + lineIndex * 1.2 + wordIndex * 0.3,
                    duration: 2,
                    type: "spring",
                    stiffness: 80
                  }}
                >
                  {word}
                </motion.span>
              </motion.span>
            ))}
          </motion.div>
        ))}
      </div>

      {/* Author */}
      <motion.div
        className="constellation-author"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 5 + lines.length * 0.5, duration: 2 }}
      >
        ✨ {poem?.author || 'Anonymous'} ✨
      </motion.div>

      {/* Audio element for crystal sounds */}
      <audio ref={audioRef} preload="auto" volume="0.3">
        <source src="/sounds/crystal.mp3" type="audio/mpeg" />
        <source src="/sounds/crystal.ogg" type="audio/ogg" />
      </audio>

      {/* Back button */}
      <motion.button
        className="constellation-back-button"
        onClick={onBack}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        whileHover={{ textShadow: "0 0 8px rgba(147, 197, 253, 0.8)" }}
      >
        ← Return to Earth
      </motion.button>
    </motion.div>
  );
};

export default ConstellationsView;
